import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable} from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class SpatialDiffService {
    constructor(private http: HttpClient) {}

    // Worker function that will be executed in the web worker
    layerDiffWorkerFunction = function () {
        onmessage = function (message) {
            console.info('Message from main thread:', message.data);

            const {snapshotFeatures, currentFeatures} = message.data;

            const snapshotFeatureMap = snapshotFeatures.reduce((acc, v) => {
                acc[v.attributes.id] = v;
                return acc;
            }, {});

            const currentFeatureMap = currentFeatures.reduce((acc, v) => {
                acc[v.attributes.id] = v;
                return acc;
            }, {});

            const allFeatureIds = snapshotFeatures
                .concat(currentFeatures)
                .map(v => v.attributes.id)
                .reduce((acc, v) => {
                    if (!acc.includes(v)) acc.push(v);
                    return acc;
                }, [])
                .sort((a, b) => a - b);

            const diff = allFeatureIds.reduce(
                (acc, featureId) => {
                    const snapshotFeature = snapshotFeatureMap[featureId];
                    const currentFeature = currentFeatureMap[featureId];

                    if (snapshotFeature && currentFeature) {
                        // Use Object.assign (not object spread) so this survives being stringified into the web
                        // worker blob - esbuild compiles `{...obj}` to a `__spreadValues` helper that isn't defined
                        // in the isolated worker scope.
                        const snapshotFeatureObjectIdUpdated = Object.assign({}, snapshotFeature);
                        snapshotFeatureObjectIdUpdated.attributes.objectid = currentFeature.attributes.objectid;
                        if (JSON.stringify(snapshotFeatureObjectIdUpdated) === JSON.stringify(currentFeature)) {
                            acc.unchanged.push(snapshotFeatureObjectIdUpdated);
                        } else {
                            acc.updates.push(snapshotFeatureObjectIdUpdated);
                        }
                    } else if (currentFeature) {
                        acc.deletes.push(currentFeature);
                    } else {
                        acc.adds.push(snapshotFeature);
                    }

                    return acc;
                },
                {adds: [], updates: [], deletes: [], unchanged: []}
            );

            postMessage(diff);
        };
    };

    private layerDiffWorkerUrl = URL.createObjectURL(
        new Blob([`(${this.layerDiffWorkerFunction})()`], {
            type: 'application/javascript'
        })
    );

    /**
     * Diff snapshot features and current features
     * @param layerName - the layer name
     * @param snapshotFeatures - the snapshot features
     * @param currentFeatures - the current features
     * @return {Observable} that resolves with layer diff {Object} { adds: [], updates: [], deletes: [], unchanged: [] }
     */
    getLayerDiff(layerName: string, snapshotFeatures: any[], currentFeatures: any[]): Observable<any> {
        return new Observable(observer => {
            console.info(`SpatialDiffService - getLayerDiff(), layer name: ${layerName}`);

            const worker = new Worker(this.layerDiffWorkerUrl);

            worker.onmessage = message => {
                console.info(`Message from worker thread for ${layerName}`, message.data);
                worker.terminate();
                observer.next(message.data);
                observer.complete();
            };

            worker.onerror = err => {
                observer.error(err);
                worker.terminate();
            };

            worker.postMessage({
                layerName: layerName,
                snapshotFeatures: snapshotFeatures,
                currentFeatures: currentFeatures
            });
        });
    }
}
